import React from "react";
import { Modal, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { CalendarBlankIcon, ClockIcon, GraduationCapIcon, UsersIcon } from "@phosphor-icons/react";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomDropdown from "../../common/general/CustomDropDown/CustomDropdown";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CourseTermSelect from "./CourseTermSelect";
import type { CourseTermOption } from "./courseTermOptions";

export type CourseAudience = "traditionalStudent" | "workingProfessional" | "graduateStudent" | "mixedOpen";
export type DeliveryMode = "fullyOnline" | "hybrid" | "inPerson";
export type CourseLengthValue = "5" | "8" | "10" | "12" | "16" | "selfPaced";

export type CourseContextState = {
  targetAudience: CourseAudience;
  courseLengthWeeks: CourseLengthValue;
  interactionsPerWeek: number;
  deliveryMode: DeliveryMode;
  creditHours: number;
};

export type CarnegieWorkload = {
  effectiveWeeks: number;
  directContactHours: number;
  outOfClassHours: number;
  totalHours: number;
};

type CourseContextEvaluationModalProps = {
  visible: boolean;
  onClose: () => void;
  onStartEvaluation: () => void;
  startEvaluationDisabled?: boolean;
  selectedTerm: string | null;
  termOptions: CourseTermOption[];
  courseTermDropdownOpen: boolean;
  setCourseTermDropdownOpen: (open: boolean) => void;
  onCourseTermChange: (term: string) => void;
  onEditTermOnCourseRow: () => void;
  courseContext: CourseContextState;
  setCourseContext: React.Dispatch<React.SetStateAction<CourseContextState>>;
  carnegieWorkload: CarnegieWorkload;
  courseLengthDropdownOpen: boolean;
  setCourseLengthDropdownOpen: (open: boolean) => void;
  interactionsDropdownOpen: boolean;
  setInteractionsDropdownOpen: (open: boolean) => void;
  deliveryModeDropdownOpen: boolean;
  setDeliveryModeDropdownOpen: (open: boolean) => void;
  creditHoursDropdownOpen: boolean;
  setCreditHoursDropdownOpen: (open: boolean) => void;
};

const COURSE_LENGTH_OPTIONS: { label: string; value: CourseLengthValue }[] = [
  { label: "5 weeks", value: "5" },
  { label: "8 weeks", value: "8" },
  { label: "10 weeks", value: "10" },
  { label: "12 weeks", value: "12" },
  { label: "16 weeks", value: "16" },
  { label: "Self-paced", value: "selfPaced" },
];
const INTERACTIONS_OPTIONS = [1, 2, 3, 4, 5] as const;
const CREDIT_HOURS_OPTIONS = [1, 2, 3, 4, 5] as const;

const TARGET_AUDIENCE_OPTIONS: { value: CourseAudience; label: string; subtitle: string }[] = [
  {
    value: "traditionalStudent",
    label: "Traditional Student",
    subtitle: "Full-time, campus-based learners",
  },
  {
    value: "workingProfessional",
    label: "Working Professional",
    subtitle: "Part-time, balancing work and study",
  },
  {
    value: "graduateStudent",
    label: "Graduate Student",
    subtitle: "Advanced learners with prior knowledge",
  },
  {
    value: "mixedOpen",
    label: "Mixed / Open",
    subtitle: "Diverse backgrounds and schedules",
  },
];

const DELIVERY_MODE_OPTIONS: { value: DeliveryMode; label: string }[] = [
  { value: "fullyOnline", label: "Fully Online" },
  { value: "hybrid", label: "Hybrid" },
  { value: "inPerson", label: "In Person" },
];

function CourseContextEvaluationModal({
  visible,
  onClose,
  onStartEvaluation,
  startEvaluationDisabled = false,
  selectedTerm,
  termOptions,
  courseTermDropdownOpen,
  setCourseTermDropdownOpen,
  onCourseTermChange,
  onEditTermOnCourseRow,
  courseContext,
  setCourseContext,
  carnegieWorkload,
  courseLengthDropdownOpen,
  setCourseLengthDropdownOpen,
  interactionsDropdownOpen,
  setInteractionsDropdownOpen,
  deliveryModeDropdownOpen,
  setDeliveryModeDropdownOpen,
  creditHoursDropdownOpen,
  setCreditHoursDropdownOpen,
}: CourseContextEvaluationModalProps) {
  const handleCourseTermDropdownOpenChange = (open: boolean) => {
    setCourseTermDropdownOpen(open);
    if (open) {
      setCourseLengthDropdownOpen(false);
      setInteractionsDropdownOpen(false);
      setDeliveryModeDropdownOpen(false);
      setCreditHoursDropdownOpen(false);
    }
  };

  return (
    <Modal transparent={true} visible={visible} animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <View>
              <CustomText
                text="Course Context"
                size="s"
                textType="display"
                weight="bold"
                style={styles.modalTitle}
                useTranslationText={false}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomText
                text="Help us tailor the evaluation by providing details about your course structure and audience."
                size="s"
                style={styles.modalSubtitle}
                useTranslationText={false}
              />
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel="Close modal"
              onPress={onClose}
              style={styles.modalCloseButton}>
              <CustomText text="×" size="xxl" style={styles.modalCloseButtonText} useTranslationText={false} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
            {!selectedTerm ? (
              <>
                <View style={[styles.termWarningCard, courseTermDropdownOpen && styles.termWarningCardOpen]}>
                  <View style={styles.termWarningRow}>
                    <CalendarBlankIcon size={18} color="#C2410C" weight="bold" />
                    <View style={styles.termWarningContent}>
                      <CustomText
                        text="No term set for this course"
                        size="s"
                        weight="medium"
                        style={styles.termWarningTitle}
                        useTranslationText={false}
                      />
                      <CustomSpacing type="vertical" size="xs" />
                      <CustomText
                        text="Term-based checks (due dates within term range, holiday conflicts, weekend due dates) will be skipped. You can set one now or run the evaluation without these checks."
                        size="s"
                        style={styles.termWarningDescription}
                        useTranslationText={false}
                      />
                      <View style={styles.termWarningActionsRow}>
                        <CourseTermSelect
                          value={selectedTerm}
                          options={termOptions}
                          open={courseTermDropdownOpen}
                          onOpenChange={handleCourseTermDropdownOpenChange}
                          onChange={onCourseTermChange}
                          variant="modal"
                          accessibilityLabel="Select course term"
                        />
                        <TouchableOpacity
                          accessibilityRole="button"
                          accessibilityLabel="Edit term on the course row"
                          onPress={onEditTermOnCourseRow}
                          style={styles.editTermRowButton}>
                          <CustomText
                            text="Edit on the course row"
                            size="s"
                            weight="medium"
                            style={styles.editTermRowButtonText}
                            useTranslationText={false}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                </View>
                <View style={styles.sectionGap} />
              </>
            ) : null}

            <View style={styles.sectionHeadingRow}>
              <UsersIcon size={18} color="#344054" />
              <CustomText
                text="Target Audience"
                size="s"
                weight="bold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />
            </View>
            <View style={styles.audienceGrid}>
              {TARGET_AUDIENCE_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  accessibilityRole="button"
                  accessibilityLabel={option.label}
                  style={[
                    styles.audienceCard,
                    courseContext.targetAudience === option.value && styles.selectedOptionCard,
                  ]}
                  onPress={() => setCourseContext((prev) => ({ ...prev, targetAudience: option.value }))}>
                  <CustomText text={option.label} size="xs" weight="medium" useTranslationText={false} />
                  <CustomSpacing type="vertical" size="xs" />
                  <CustomText
                    text={option.subtitle}
                    size="xs"
                    style={styles.optionSubtitle}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.sectionGap} />
            <View style={styles.sectionHeadingRow}>
              <ClockIcon size={18} color="#344054" />
              <CustomText
                text="Duration & Frequency"
                size="s"
                weight="bold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />
            </View>
            <View style={styles.controlGrid}>
              <View style={[styles.controlField, courseLengthDropdownOpen && styles.controlFieldTop]}>
                <CustomText text="Course Length" size="xs" weight="medium" useTranslationText={false} />
                <CustomDropdown
                  options={COURSE_LENGTH_OPTIONS}
                  value={courseContext.courseLengthWeeks}
                  onChange={(value) =>
                    setCourseContext((prev) => ({ ...prev, courseLengthWeeks: value as CourseLengthValue }))
                  }
                  placeholder="Select course length"
                  accessibilityLabel="Course Length"
                  open={courseLengthDropdownOpen}
                  onOpenChange={(open) => {
                    setCourseLengthDropdownOpen(open);
                    if (open) {
                      setCourseTermDropdownOpen(false);
                      setInteractionsDropdownOpen(false);
                      setDeliveryModeDropdownOpen(false);
                      setCreditHoursDropdownOpen(false);
                    }
                  }}
                  fullWidth={true}
                />
              </View>

              <View style={[styles.controlField, interactionsDropdownOpen && styles.controlFieldTop]}>
                <CustomText text="Interactions per Week" size="xs" weight="medium" useTranslationText={false} />
                <CustomDropdown
                  options={INTERACTIONS_OPTIONS.map((count) => ({ label: `${count} per week`, value: `${count}` }))}
                  value={`${courseContext.interactionsPerWeek}`}
                  onChange={(value) => setCourseContext((prev) => ({ ...prev, interactionsPerWeek: Number(value) }))}
                  placeholder="Select frequency"
                  accessibilityLabel="Interactions per Week"
                  open={interactionsDropdownOpen}
                  onOpenChange={(open) => {
                    setInteractionsDropdownOpen(open);
                    if (open) {
                      setCourseTermDropdownOpen(false);
                      setCourseLengthDropdownOpen(false);
                      setDeliveryModeDropdownOpen(false);
                      setCreditHoursDropdownOpen(false);
                    }
                  }}
                  fullWidth={true}
                />
              </View>

              <View style={[styles.controlField, deliveryModeDropdownOpen && styles.controlFieldTop]}>
                <CustomText text="Delivery Mode" size="xs" weight="medium" useTranslationText={false} />
                <CustomDropdown
                  options={DELIVERY_MODE_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
                  value={courseContext.deliveryMode}
                  onChange={(value) => setCourseContext((prev) => ({ ...prev, deliveryMode: value as DeliveryMode }))}
                  placeholder="Select delivery mode"
                  accessibilityLabel="Delivery Mode"
                  open={deliveryModeDropdownOpen}
                  onOpenChange={(open) => {
                    setDeliveryModeDropdownOpen(open);
                    if (open) {
                      setCourseTermDropdownOpen(false);
                      setCourseLengthDropdownOpen(false);
                      setInteractionsDropdownOpen(false);
                      setCreditHoursDropdownOpen(false);
                    }
                  }}
                  fullWidth={true}
                />
              </View>

              <View style={[styles.controlField, creditHoursDropdownOpen && styles.controlFieldTop]}>
                <CustomText text="Credit Hours" size="xs" weight="medium" useTranslationText={false} />
                <CustomDropdown
                  options={CREDIT_HOURS_OPTIONS.map((hours) => ({
                    label: `${hours} credit hour${hours > 1 ? "s" : ""}`,
                    value: `${hours}`,
                  }))}
                  value={`${courseContext.creditHours}`}
                  onChange={(value) => setCourseContext((prev) => ({ ...prev, creditHours: Number(value) }))}
                  placeholder="Select credit hours"
                  accessibilityLabel="Credit Hours"
                  open={creditHoursDropdownOpen}
                  onOpenChange={(open) => {
                    setCreditHoursDropdownOpen(open);
                    if (open) {
                      setCourseTermDropdownOpen(false);
                      setCourseLengthDropdownOpen(false);
                      setInteractionsDropdownOpen(false);
                      setDeliveryModeDropdownOpen(false);
                    }
                  }}
                  fullWidth={true}
                />
              </View>
            </View>

            <View style={styles.sectionGap} />
            <View style={styles.sectionHeadingRow}>
              <GraduationCapIcon size={18} color="#344054" />
              <CustomText
                text="Carnegie Credit Workload"
                size="s"
                weight="bold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />
            </View>
            <View style={styles.workloadCard}>
              <View style={styles.workloadStatsRow}>
                <View style={styles.workloadStatItem}>
                  <CustomText
                    text={`${carnegieWorkload.directContactHours}`}
                    size="xl"
                    weight="ultraBold"
                    style={styles.workloadStatValue}
                    useTranslationText={false}
                  />
                  <CustomText
                    text="Direct Contact Hours"
                    size="xs"
                    style={styles.optionSubtitle}
                    useTranslationText={false}
                  />
                </View>
                <View style={styles.workloadStatItem}>
                  <CustomText
                    text={`${carnegieWorkload.outOfClassHours}`}
                    size="xl"
                    weight="ultraBold"
                    style={styles.workloadStatValue}
                    useTranslationText={false}
                  />
                  <CustomText
                    text="Out-of-Class Hours"
                    size="xs"
                    style={styles.optionSubtitle}
                    useTranslationText={false}
                  />
                </View>
                <View style={styles.workloadStatItem}>
                  <CustomText
                    text={`${carnegieWorkload.totalHours}`}
                    size="xl"
                    weight="ultraBold"
                    style={styles.workloadStatValueHighlight}
                    useTranslationText={false}
                  />
                  <CustomText
                    text="Total Workload"
                    size="xs"
                    style={styles.optionSubtitle}
                    useTranslationText={false}
                  />
                </View>
              </View>
              <CustomSpacing type="vertical" size="s" />
              <CustomText
                text={`Based on ${courseContext.creditHours} credits x ${
                  courseContext.courseLengthWeeks === "selfPaced"
                    ? "Self-paced (16-week baseline)"
                    : `${courseContext.courseLengthWeeks} weeks`
                }. Carnegie standard: 1 credit = 1 contact hour + 2 out-of-class hours per week.`}
                size="xs"
                style={styles.workloadHint}
                useTranslationText={false}
              />
            </View>
            <CustomSpacing type="vertical" size="s" />
            <CustomText
              text="The evaluation will flag courses where content volume doesn't match the expected workload for the credit hours awarded."
              size="xs"
              style={styles.optionSubtitle}
              useTranslationText={false}
            />

            <CustomSpacing type="vertical" size="xxl" />

            <CustomButton
              text="Start Evaluation"
              styleType="primary"
              size="s"
              style={styles.startEvaluationButton}
              useTranslationText={false}
              disabled={startEvaluationDisabled}
              onPress={onStartEvaluation}
            />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(16, 24, 40, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  modalContainer: {
    width: "100%",
    maxWidth: 600,
    maxHeight: "86%",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    overflow: "visible",
  },
  modalHeader: {
    backgroundColor: "#F9FAFB",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalTitle: {
    color: "#101828",
    fontSize: 20,
    lineHeight: 26,
  },
  modalSubtitle: {
    color: "#667085",
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  modalCloseButtonText: {
    color: "#98A2B3",
  },
  modalBody: {
    flex: 1,
    overflow: "visible",
  },
  modalBodyContent: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    overflow: "visible",
  },
  termWarningCard: {
    borderWidth: 1,
    borderColor: "#FDE68A",
    borderRadius: 16,
    backgroundColor: "#FFFAEB",
    paddingHorizontal: 16,
    paddingVertical: 16,
    position: "relative",
    zIndex: 1,
  },
  termWarningCardOpen: {
    zIndex: 100000,
    elevation: 100000,
  },
  termWarningRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  termWarningContent: {
    flex: 1,
  },
  termWarningTitle: {
    color: "#9A3412",
    fontSize: 14,
    lineHeight: 20,
  },
  termWarningDescription: {
    color: "#9A3412",
    fontSize: 13,
    lineHeight: 19,
  },
  termWarningActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
  },
  editTermRowButton: {
    paddingHorizontal: 0,
    paddingVertical: 6,
  },
  editTermRowButtonText: {
    color: "#9A3412",
    fontSize: 13,
    lineHeight: 18,
  },
  sectionTitle: {
    color: "#101828",
  },
  sectionHeadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  sectionGap: {
    height: 18,
  },
  audienceGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  audienceCard: {
    width: "49%",
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  selectedOptionCard: {
    borderColor: "#B2CCFF",
    backgroundColor: "#F5F8FF",
  },
  optionSubtitle: {
    color: "#667085",
    fontSize: 11,
  },
  controlGrid: {
    marginTop: 10,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    position: "relative",
    zIndex: 1,
  },
  controlField: {
    width: "49%",
    gap: 4,
    position: "relative",
    zIndex: 1,
  },
  controlFieldTop: {
    zIndex: 100000,
    elevation: 100000,
  },
  workloadCard: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: "#F8FBFF",
  },
  workloadStatsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  workloadStatItem: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  workloadStatValue: {
    color: "#101828",
  },
  workloadStatValueHighlight: {
    color: "#2952CC",
  },
  workloadHint: {
    color: "#667085",
    textAlign: "center",
    fontSize: 11,
  },
  startEvaluationButton: {
    width: "100%",
    justifyContent: "center",
    borderRadius: 10,
  },
});

export const DEFAULT_COURSE_CONTEXT: CourseContextState = {
  targetAudience: "traditionalStudent",
  courseLengthWeeks: "5",
  interactionsPerWeek: 3,
  deliveryMode: "fullyOnline",
  creditHours: 3,
};

export default CourseContextEvaluationModal;
